import moment from "moment";

import DivisionServiceBlockDetail from "issara-sdk/apis/DivisionServiceBlockDetail_apps_QUE";
import DoctorNoteTemplateGroupList from "issara-sdk/apis/DoctorNoteTemplateGroupList_apps_APP";
import DoctorNoteTemplateList from "issara-sdk/apis/DoctorNoteTemplateList_apps_APP";
import PatientAppointmentUpdate from "issara-sdk/apis/PatientAppointmentUpdate_apps_QUE";
import PatientAppointmentV2List from "issara-sdk/apis/PatientAppointmentV2List_apps_QUE";
import PatientAppointmentView from "issara-sdk/apis/PatientAppointmentView_apps_QUE";
import Scheduling from "issara-sdk/apis/Scheduling_apps_QUE";
import DoctorNoteTemplateGroupSerializer from "issara-sdk/types/DoctorNoteTemplateGroupSerializer_apps_APP";
import DoctorNoteTemplateSerializer from "issara-sdk/types/DoctorNoteTemplateSerializer_apps_APP";
import PatientAppointmentSerializer from "issara-sdk/types/PatientAppointmentSerializer_apps_QUE";

import {
  ActionHandler,
  BaseSeqState,
  BLC_STATUS,
  GenerateActionTypes,
  generatePrefixedActions,
  PickMainState,
} from "react-lib/apps/HISV3/common/CommonInterface";

import {
  isDueToday,
  type AdvanceOrder,
  type AppointmentKind,
} from "../../patient/orderAppointmentModelV4";

/** ============== Type-Defining Constants =============  */

export const ACTIONS = {
  LOAD_APPOINTMENTS: "LOAD_APPOINTMENTS",
  LOAD_SLOTS: "LOAD_SLOTS",
  LOAD_SLOT_DETAIL: "LOAD_SLOT_DETAIL",
  LOAD_NOTE_TEMPLATES: "LOAD_NOTE_TEMPLATES",
  SAVE: "SAVE",
  CANCEL: "CANCEL",
  MARK_PULLED: "MARK_PULLED",
} as const;

export const SEQUENCE_NAME = "OrderAppointmentV4" as const;

/** =============== Types and Interfaces ===============  */
export type ProviderRecord = Record<string, any>;

export type State = Partial<{
  OrderAppointmentV4Sequence: Partial<{
    sequenceIndex: "Action" | "START" | null;
    encounterId: number | null;
    /** นัดของผู้ป่วยที่สร้างจาก Encounter นี้ */
    appointments: PatientAppointmentSerializer[];
    /** นัดวันนี้ที่มีคำสั่งล่วงหน้ารอดึงเข้า Encounter นี้ */
    dueAppointments: PatientAppointmentSerializer[];
    providers: ProviderRecord[];
    /** DivisionServiceBlockFilter ของช่วงที่แสดง */
    dsb: Record<string, any>[];
    dsbRange: { start: string; end: string } | null;
    /** division-service-block-detail ตาม dsb id */
    slotDetails: Record<number, Record<string, any>>;
    noteTemplates: DoctorNoteTemplateSerializer[];
    noteGroups: DoctorNoteTemplateGroupSerializer[];
  }> | null;
}>;

export type PickedState = PickMainState<"buttonLoadCheck" | "errorMessage" | "selectedEncounter">;

export type Event =
  | { message: "RunSequence"; params: Record<string, unknown> }
  | { message: "GetMasterData"; params: Record<string, unknown> };

export type Data = { division?: number; device?: number };

export type SeqState = BaseSeqState<SequenceName> & { encounterId?: number };

export type Actions = typeof ACTIONS;

/** ค่าที่บันทึกลงนัด */
export type AppointmentForm = {
  id?: number;
  kind: AppointmentKind;
  dsbId: number;
  /** YYYY-MM-DD */
  date: string;
  /** HH:mm */
  time: string;
  duration: number;
  type: string;
  appointmentFor: string;
  advice: string;
  orders: AdvanceOrder[];
};

type SequenceName = typeof SEQUENCE_NAME;

type ActionPayloadMap = {
  [ACTIONS.LOAD_APPOINTMENTS]: {};
  /** YYYY-MM-DD */
  [ACTIONS.LOAD_SLOTS]: { start: string; end: string };
  [ACTIONS.LOAD_SLOT_DETAIL]: { dsbId: number };
  [ACTIONS.LOAD_NOTE_TEMPLATES]: {};
  [ACTIONS.SAVE]: {
    form: AppointmentForm;
    onDone: (result: { id?: number; error?: unknown }) => void;
  };
  [ACTIONS.CANCEL]: {
    id: number;
    status: number;
    reason: string;
    onDone: (result: { error?: unknown }) => void;
  };
  [ACTIONS.MARK_PULLED]: { ids: number[] };
};

type ActionTypes = GenerateActionTypes<
  Actions,
  ActionPayloadMap,
  SequenceName,
  PickedState & State,
  SeqState
>;

type ActionType = ActionTypes["ActionType"];

export type RunSequence = ActionTypes["RunSequence"];

export type SetProp = ActionTypes["SetProp"];

type Params<A extends ActionType["action"]> = ActionTypes["Params"][A];

export type Handler<P = unknown, R = void> = ActionHandler<PickedState & State, Event, Data, P, R>;

type ApiResult = [any, any, any?];

/** ===================== Constants ====================  */
export const StateInitial: State = {
  OrderAppointmentV4Sequence: null,
};

export const DataInitial: Data = {};

export const BTN_ACTS = generatePrefixedActions(ACTIONS, SEQUENCE_NAME);

/** ===================== Functions ====================  */
export const Start: Handler<SeqState> = async (controller, params) => {
  controller.handleEvent({
    message: "GetMasterData",
    params: {
      masters: [
        ["patientAppType", {}],
        ["appointmentForNote", {}],
      ],
    },
  });

  controller.setState(
    {
      OrderAppointmentV4Sequence: {
        sequenceIndex: "Action",
        encounterId: params.encounterId ?? null,
        appointments: [],
        dueAppointments: [],
        providers: [],
        dsb: [],
        dsbRange: null,
        slotDetails: {},
        noteTemplates: [],
        noteGroups: [],
      },
    },
    () => {
      HandleLoadAppointments(controller, {} as Params<Actions["LOAD_APPOINTMENTS"]>);
      loadProviders(controller);
    },
  );
};

const loadProviders = async (controller: Parameters<Handler>[0]) => {
  const division = controller.data.division;
  const [res]: ApiResult = await Scheduling.get({
    command: "ProviderSearch",
    params: { division: controller.data.division },
    apiToken: controller.apiToken,
    extra: { division: controller.data.division, device: controller.data.device },
  });

  if (controller.data.division !== division) return;
  const state = controller.getState();

  controller.setState({
    OrderAppointmentV4Sequence: {
      ...state.OrderAppointmentV4Sequence,
      providers: Array.isArray(res) ? res : (res?.items ?? []),
    },
  });
};

/** ====================================================  */
/**                        Action                         */
/** ====================================================  */
export const Action: Handler<ActionType> = async (controller, params) => {
  const actionHandlers: Partial<{ [K in ActionType["action"]]: Handler<Params<K>> }> = {
    [ACTIONS.LOAD_APPOINTMENTS]: HandleLoadAppointments,
    [ACTIONS.LOAD_SLOTS]: HandleLoadSlots,
    [ACTIONS.LOAD_SLOT_DETAIL]: HandleLoadSlotDetail,
    [ACTIONS.LOAD_NOTE_TEMPLATES]: HandleLoadNoteTemplates,
    [ACTIONS.SAVE]: HandleSave,
    [ACTIONS.CANCEL]: HandleCancel,
    [ACTIONS.MARK_PULLED]: HandleMarkPulled,
  };

  const { action } = params;

  const handler = actionHandlers[action] as Handler<Params<typeof action>> | undefined;

  if (handler) {
    return handler(controller, params);
  }

  console.error(`No handler found for action: ${action}`);
};

/** ====================================================  */
/**                        Handler                        */
/** ====================================================  */
const HandleLoadAppointments: Handler<Params<Actions["LOAD_APPOINTMENTS"]>> = async (
  controller,
) => {
  let state = controller.getState();

  const encounter = state.selectedEncounter as Record<string, any> | null;
  const patient = encounter?.patient_id ?? encounter?.patient;
  const encounterId = state.OrderAppointmentV4Sequence?.encounterId;

  if (!patient || !encounterId) {
    return;
  }

  controller.setState({
    buttonLoadCheck: {
      ...state.buttonLoadCheck,
      [BTN_ACTS.LOAD_APPOINTMENTS]: BLC_STATUS.LOADING,
    },
  });

  const [res, error]: ApiResult = await PatientAppointmentV2List.list({
    params: { patient_id: patient, limit: 100 },
    apiToken: controller.apiToken,
    extra: { division: controller.data.division, device: controller.data.device },
  });

  const items: PatientAppointmentSerializer[] = res?.items ?? [];
  const appointments = items.filter((item) => item.extra?.encounter === encounterId);
  const dueAppointments = items.filter(
    (item) => item.extra?.encounter !== encounterId && isDueToday(item),
  );

  state = controller.getState();

  controller.setState({
    OrderAppointmentV4Sequence: {
      ...state.OrderAppointmentV4Sequence,
      appointments,
      dueAppointments,
    },
    buttonLoadCheck: {
      ...state.buttonLoadCheck,
      [BTN_ACTS.LOAD_APPOINTMENTS]: error ? BLC_STATUS.ERROR : BLC_STATUS.SUCCESS,
    },
  });
};

const HandleLoadSlots: Handler<Params<Actions["LOAD_SLOTS"]>> = async (controller, params) => {
  let state = controller.getState();
  const division = controller.data.division;
  const encounterId = state.OrderAppointmentV4Sequence?.encounterId;

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.LOAD_SLOTS]: BLC_STATUS.LOADING },
  });

  const [res, error]: ApiResult = await Scheduling.get({
    command: "DivisionServiceBlockFilter",
    params: { divisions: controller.data.division, start_date: params.start, end_date: params.end },
    apiToken: controller.apiToken,
    extra: { division: controller.data.division, device: controller.data.device },
  });

  state = controller.getState();
  if (controller.data.division !== division || state.OrderAppointmentV4Sequence?.encounterId !== encounterId) return;

  controller.setState({
    OrderAppointmentV4Sequence: {
      ...state.OrderAppointmentV4Sequence,
      dsb: Array.isArray(res) ? res : (res?.items ?? []),
      dsbRange: { start: params.start, end: params.end },
    },
    buttonLoadCheck: {
      ...state.buttonLoadCheck,
      [BTN_ACTS.LOAD_SLOTS]: error ? BLC_STATUS.ERROR : BLC_STATUS.SUCCESS,
    },
  });
};

const HandleLoadSlotDetail: Handler<Params<Actions["LOAD_SLOT_DETAIL"]>> = async (
  controller,
  params,
) => {
  const [res]: ApiResult = await DivisionServiceBlockDetail.retrieve({
    pk: params.dsbId,
    apiToken: controller.apiToken,
    extra: { division: controller.data.division, device: controller.data.device },
  });

  const state = controller.getState();

  controller.setState({
    OrderAppointmentV4Sequence: {
      ...state.OrderAppointmentV4Sequence,
      slotDetails: { ...state.OrderAppointmentV4Sequence?.slotDetails, [params.dsbId]: res ?? {} },
    },
  });
};

const HandleLoadNoteTemplates: Handler<Params<Actions["LOAD_NOTE_TEMPLATES"]>> = async (
  controller,
) => {
  const common = {
    apiToken: controller.apiToken,
    extra: { division: controller.data.division, device: controller.data.device },
  };

  const [[notes], [groups]]: ApiResult[] = await Promise.all([
    DoctorNoteTemplateList.list(common),
    DoctorNoteTemplateGroupList.list(common),
  ]);

  const state = controller.getState();

  controller.setState({
    OrderAppointmentV4Sequence: {
      ...state.OrderAppointmentV4Sequence,
      noteTemplates: notes?.items ?? [],
      noteGroups: groups?.items ?? [],
    },
  });
};

const HandleSave: Handler<Params<Actions["SAVE"]>> = async (controller, params) => {
  let state = controller.getState();

  const { form } = params;
  const seq = state.OrderAppointmentV4Sequence;
  const original = seq?.appointments?.find((item) => item.id === form.id);
  const encounter = state.selectedEncounter as Record<string, any> | null;

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.SAVE]: BLC_STATUS.LOADING },
  });

  const data = {
    patient: encounter?.patient_id ?? encounter?.patient,
    division: controller.data.division,
    type: Number(form.type) || null,
    division_service_block: form.dsbId,
    estimated_at_iso: moment(`${form.date} ${form.time}`, "YYYY-MM-DD HH:mm").format(),
    estimated_duration: form.duration,
    order_note: form.advice,
    extra: {
      ...original?.extra,
      encounter: seq?.encounterId,
      kind: form.kind,
      appointmentFor: form.appointmentFor,
      orders: form.orders,
    },
  };

  const common = {
    apiToken: controller.apiToken,
    extra: { division: controller.data.division, device: controller.data.device },
  };

  const [res, error]: ApiResult = form.id
    ? await PatientAppointmentUpdate.patch({ ...common, pk: form.id, data: data as any })
    : await PatientAppointmentView.create({
        ...common,
        data: {
          ...data,
          encounter_id: seq?.encounterId,
          status: 1,
          repetition_note: "",
          send_message: false,
        } as any,
      });

  state = controller.getState();

  controller.setState({
    buttonLoadCheck: {
      ...state.buttonLoadCheck,
      [BTN_ACTS.SAVE]: error ? BLC_STATUS.ERROR : BLC_STATUS.SUCCESS,
    },
  });

  if (!error) {
    await HandleLoadAppointments(controller, {} as Params<Actions["LOAD_APPOINTMENTS"]>);
  }

  params.onDone({ id: res?.id ?? form.id, error });
};

const HandleCancel: Handler<Params<Actions["CANCEL"]>> = async (controller, params) => {
  let state = controller.getState();

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.CANCEL]: BLC_STATUS.LOADING },
  });

  const [, error]: ApiResult = await PatientAppointmentUpdate.patch({
    pk: params.id,
    data: { status: params.status, status_note: params.reason } as any,
    apiToken: controller.apiToken,
    extra: { division: controller.data.division, device: controller.data.device },
  });

  state = controller.getState();

  controller.setState({
    buttonLoadCheck: {
      ...state.buttonLoadCheck,
      [BTN_ACTS.CANCEL]: error ? BLC_STATUS.ERROR : BLC_STATUS.SUCCESS,
    },
  });

  if (!error) {
    await HandleLoadAppointments(controller, {} as Params<Actions["LOAD_APPOINTMENTS"]>);
  }

  params.onDone({ error });
};

const HandleMarkPulled: Handler<Params<Actions["MARK_PULLED"]>> = async (controller, params) => {
  const state = controller.getState();

  const seq = state.OrderAppointmentV4Sequence;
  const due = (seq?.dueAppointments ?? []).filter((item) => params.ids.includes(Number(item.id)));

  controller.setState({
    OrderAppointmentV4Sequence: {
      ...seq,
      dueAppointments: (seq?.dueAppointments ?? []).filter((item) => !due.includes(item)),
    },
  });

  await Promise.all(
    due.map((item) =>
      PatientAppointmentUpdate.patch({
        pk: item.id,
        data: { extra: { ...item.extra, pulledEncounter: seq?.encounterId } } as any,
        apiToken: controller.apiToken,
        extra: { division: controller.data.division, device: controller.data.device },
      }),
    ),
  );
};
